'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive DropZone
 * ROLE: presentation wrapper | ARIA: visual overlay is aria-hidden; its label is announced via the live region
 * KEYBOARD: hidden file input is keyboard-reachable (clip-path: inset(50%));
 *           consumers wire a visible trigger button next to it
 * FOCUS: not focusable itself; focus stays on the originating trigger
 * LIVE-REGION: polite status announces the overlay label while a file drag is over
 *              the zone (consumer surfaces upload progress via its own aria-live)
 * RTL: drop overlay is direction-agnostic
 * RSC: CLIENT — drag/drop event handlers
 *
 * Wraps children with native HTML5 drag-and-drop. Fires `onFiles` with the
 * array of dropped File objects. The drop overlay renders only while a drag
 * is active over the wrapper. Consumers pass an `accept` MIME-type filter to
 * pre-filter the dropped set; rejected files are dropped silently (the
 * caller's per-file error UI handles surfacing).
 */
import { useCallback, useRef, useState, } from 'react';
import { VisuallyHidden } from '../../VisuallyHidden/VisuallyHidden';
function fileMatches(file, accept) {
    if (accept === undefined || accept.trim().length === 0)
        return true;
    const tokens = accept
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0);
    if (tokens.length === 0)
        return true;
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    for (const t of tokens) {
        // Extension match: ".pdf"
        if (t.startsWith('.')) {
            if (fileName.endsWith(t))
                return true;
            continue;
        }
        // Wildcard match: "image/*"
        if (t.endsWith('/*')) {
            const prefix = t.slice(0, -1);
            if (fileType.startsWith(prefix))
                return true;
            continue;
        }
        if (fileType === t)
            return true;
    }
    return false;
}
export function DropZone({ onFiles, accept, disabled = false, style, className, overlayLabel = 'Drop files to attach', children, }) {
    const [active, setActive] = useState(false);
    // Track nested drag-enter/leave so children entering doesn't toggle active off.
    const depthRef = useRef(0);
    const onDragEnter = useCallback((e) => {
        if (disabled)
            return;
        // Only react to drags carrying files.
        if (!e.dataTransfer.types.includes('Files'))
            return;
        e.preventDefault();
        depthRef.current += 1;
        setActive(true);
    }, [disabled]);
    const onDragLeave = useCallback((e) => {
        if (disabled)
            return;
        e.preventDefault();
        depthRef.current = Math.max(0, depthRef.current - 1);
        if (depthRef.current === 0)
            setActive(false);
    }, [disabled]);
    const onDragOver = useCallback((e) => {
        if (disabled)
            return;
        if (!e.dataTransfer.types.includes('Files'))
            return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, [disabled]);
    const onDrop = useCallback((e) => {
        if (disabled)
            return;
        e.preventDefault();
        depthRef.current = 0;
        setActive(false);
        const dropped = Array.from(e.dataTransfer.files);
        const filtered = dropped.filter((f) => fileMatches(f, accept));
        if (filtered.length > 0)
            onFiles(filtered);
    }, [disabled, accept, onFiles]);
    return (_jsxs("div", { onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: onDragOver, onDrop: onDrop, style: { position: 'relative', ...style }, className: className, children: [children, _jsx(VisuallyHidden, { role: "status", "aria-live": "polite", children: active ? overlayLabel : '' }), active ? (_jsx("div", { "aria-hidden": "true", style: {
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-lg)',
                    background: 'color-mix(in srgb, var(--primary) 10%, var(--card))',
                    border: '2px dashed var(--primary)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--primary)',
                    fontWeight: 600,
                    pointerEvents: 'none',
                    zIndex: 1,
                }, children: overlayLabel })) : null] }));
}
/**
 * Hidden file input styled for a11y per the DS rule: never `display: none`.
 * Uses `clip-path: inset(50%)` so the input remains keyboard-focusable while
 * being visually invisible. Consumers position the visible trigger button
 * next to this and call `.click()` on the ref to open the picker.
 */
export const HIDDEN_FILE_INPUT_STYLE = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    border: 0,
};
//# sourceMappingURL=DropZone.js.map